// =========================================================
// Orders.jsx
// DEBUTS CROP
// MIS COMPRAS / CHECKOUTS
//
// IMPORTANTE:
// Este proyecto NO utiliza Order.
// Todo el proceso de compra está basado en Checkout.
// =========================================================

import {
  ArrowLeft,
  CheckCircle,
  ChevronRight,
  Clock,
  CreditCard,
  ExternalLink,
  FileText,
  MapPin,
  Package,
  Printer,
  ShieldCheck,
  Store,
  Truck,
  X,
  XCircle,
} from "lucide-react";

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import "../../styles/Orders.css";

import ProfilePageLayout from "../../components/profile/ProfilePageLayout";

// =========================================================
// API
// =========================================================
//
// DESARROLLO:
// VITE_API_URL=http://localhost:8000/api
//
// PRODUCCIÓN:
// VITE_API_URL=https://api.debutscrop.com/api
//
// =========================================================

const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  (
    import.meta.env.PROD
      ? "https://api.debutscrop.com/api"
      : "http://localhost:8000/api"
  );

// =========================================================
// HELPERS
// =========================================================

const getAuth = () => {
  try {
    const raw = localStorage.getItem("auth");

    if (!raw) {
      return null;
    }

    return JSON.parse(raw);
  } catch {
    return null;
  }
};

// =========================================================
// TOKEN
// =========================================================

const getToken = () => {
  const auth = getAuth();

  return (
    auth?.access ||
    auth?.access_token ||
    auth?.token ||
    null
  );
};

// =========================================================
// NORMALIZE API RESPONSE
// =========================================================

const normalizeList = (data) => {
  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.results)) {
    return data.results;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  if (Array.isArray(data?.items)) {
    return data.items;
  }

  return [];
};

// =========================================================
// MONEY
// =========================================================

const formatMoney = (value) => {
  const number = Number(value || 0);

  return new Intl.NumberFormat(
    "es-CO",
    {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
    }
  ).format(number);
};

// =========================================================
// DATE
// =========================================================

const formatDate = (value) => {
  if (!value) {
    return "FECHA NO DISPONIBLE";
  }

  try {
    return new Date(value)
      .toLocaleDateString(
        "es-CO",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
      )
      .toUpperCase();
  } catch {
    return "FECHA NO DISPONIBLE";
  }
};

// =========================================================
// DATE + TIME
// =========================================================

const formatDateTime = (value) => {
  if (!value) {
    return "NO DISPONIBLE";
  }

  try {
    return new Date(value)
      .toLocaleString(
        "es-CO",
        {
          dateStyle: "medium",
          timeStyle: "short",
        }
      );
  } catch {
    return "NO DISPONIBLE";
  }
};

// =========================================================
// CHECKOUT STATUS
// =========================================================

const STATUS_CONFIG = {
  pending: {
    label: "PENDIENTE",
    className: "pending",
    icon: Clock,
  },

  payment_sent: {
    label: "COMPROBANTE ENVIADO",
    className: "review",
    icon: FileText,
  },

  payment_review: {
    label: "PAGO EN REVISIÓN",
    className: "review",
    icon: Clock,
  },

  payment_approved: {
    label: "PAGO APROBADO",
    className: "approved",
    icon: ShieldCheck,
  },

  payment_rejected: {
    label: "PAGO RECHAZADO",
    className: "rejected",
    icon: XCircle,
  },

  confirmed: {
    label: "PEDIDO CONFIRMADO",
    className: "confirmed",
    icon: CheckCircle,
  },

  processing: {
    label: "PREPARANDO PEDIDO",
    className: "processing",
    icon: Package,
  },

  ready: {
    label: "LISTO",
    className: "ready",
    icon: CheckCircle,
  },

  completed: {
    label: "COMPLETADO",
    className: "completed",
    icon: CheckCircle,
  },

  cancelled: {
    label: "CANCELADO",
    className: "cancelled",
    icon: XCircle,
  },
};

// =========================================================
// STATUS
// =========================================================

const getStatusConfig = (status) => {
  return (
    STATUS_CONFIG[status] || {
      label: "PROCESO DE COMPRA",
      className: "pending",
      icon: Clock,
    }
  );
};

// =========================================================
// PAYMENT
// =========================================================

const getPaymentLabel = (paymentMethod) => {
  switch (paymentMethod) {
    case "nequi":
      return "NEQUI";

    case "transfer":
      return "TRANSFERENCIA BANCARIA";

    case "cash_store":
      return "PAGO EN TIENDA";

    default:
      return "MÉTODO NO DISPONIBLE";
  }
};

// =========================================================
// DELIVERY
// =========================================================

const getDeliveryLabel = (deliveryType) => {
  if (deliveryType === "home") {
    return "DOMICILIO";
  }

  if (deliveryType === "store") {
    return "RECOGER EN TIENDA";
  }

  return "NO DEFINIDO";
};

// =========================================================
// GOOGLE MAPS ROUTE
// =========================================================
//
// Genera una ruta hacia la dirección del checkout.
//
// No requiere Google Maps API para abrir la navegación.
// =========================================================

const buildGoogleMapsRoute = (checkout) => {
  if (checkout?.delivery_type !== "home") {
    return null;
  }

  const address = [
    checkout?.address,
    checkout?.neighborhood,
    checkout?.city,
    checkout?.department,
  ]
    .filter(Boolean)
    .join(", ");

  if (!address) {
    return null;
  }

  return (
    "https://www.google.com/maps/dir/?api=1" +
    `&destination=${encodeURIComponent(address)}` +
    "&travelmode=driving"
  );
};

// =========================================================
// COMPONENT
// =========================================================

export default function Orders() {
  const navigate = useNavigate();

  // =======================================================
  // STATE
  // =======================================================

  const [
    checkouts,
    setCheckouts,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  const [
    selectedCheckout,
    setSelectedCheckout,
  ] = useState(null);

  const [
    ticketCheckout,
    setTicketCheckout,
  ] = useState(null);

  // =======================================================
  // LOAD CHECKOUTS
  // =======================================================

  const loadCheckouts = useCallback(
    async () => {
      const token = getToken();

      if (!token) {
        navigate(
          "/login",
          {
            replace: true,
          }
        );

        return;
      }

      try {
        setLoading(true);
        setError("");

        const response = await fetch(
          `${API_BASE_URL}/checkout/`,
          {
            method: "GET",

            headers: {
              Authorization:
                `Bearer ${token}`,

              "Content-Type":
                "application/json",
            },
          }
        );

        // =============================================
        // UNAUTHORIZED
        // =============================================

        if (response.status === 401) {
          localStorage.removeItem("auth");
          localStorage.removeItem("user");

          window.dispatchEvent(
            new Event("authUpdated")
          );

          navigate(
            "/login",
            {
              replace: true,
            }
          );

          return;
        }

        // =============================================
        // ERROR
        // =============================================

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}`
          );
        }

        const data = await response.json();

        const list = normalizeList(data);

        setCheckouts(list);

      } catch (requestError) {
        console.error(
          "Error cargando checkouts:",
          requestError
        );

        setError(
          "No fue posible conectar con el historial de compras."
        );
      } finally {
        setLoading(false);
      }
    },
    [navigate]
  );

  // =======================================================
  // INITIAL LOAD
  // =======================================================

  useEffect(() => {
    loadCheckouts();
  }, [loadCheckouts]);

  // =======================================================
  // SORT
  // =======================================================

  const sortedCheckouts = useMemo(() => {
    return [...checkouts].sort(
      (a, b) => {
        const dateA =
          new Date(
            a?.created_at || 0
          ).getTime();

        const dateB =
          new Date(
            b?.created_at || 0
          ).getTime();

        return dateB - dateA;
      }
    );
  }, [checkouts]);

  // =======================================================
  // OPEN CHECKOUT
  // =======================================================

  const openCheckout = (checkout) => {
    if (!checkout?.id) {
      return;
    }

    setSelectedCheckout(checkout);
  };

  // =======================================================
  // CLOSE DETAIL
  // =======================================================

  const closeDetail = () => {
    setSelectedCheckout(null);
  };

  // =======================================================
  // OPEN TICKET
  // =======================================================

  const openTicket = (checkout) => {
    setTicketCheckout(checkout);
  };

  // =======================================================
  // CLOSE TICKET
  // =======================================================

  const closeTicket = () => {
    setTicketCheckout(null);
  };

  // =======================================================
  // PRINT TICKET
  // =======================================================

  const printTicket = () => {
    window.print();
  };

  // =======================================================
  // OPEN ROUTE
  // =======================================================

  const openRoute = (checkout) => {
    const url = buildGoogleMapsRoute(checkout);

    if (!url) {
      return;
    }

    window.open(
      url,
      "_blank",
      "noopener,noreferrer"
    );
  };

  // =======================================================
  // RENDER
  // =======================================================

  return (
    <ProfilePageLayout
      title="Mis compras"
      subtitle="Consulta tus compras, comprobantes, estados y entregas."
      icon={Package}
    >

      {/* ===================================================
          BACK BUTTON
      =================================================== */}

      <button
        type="button"
        className="profile-orders-back"
        onClick={() =>
          navigate("/profile")
        }
      >
        <ArrowLeft size={17} />

        VOLVER A MI PERFIL
      </button>

      {/* ===================================================
          LOADING
      =================================================== */}

      {loading && (
        <section
          className="profile-template-card"
        >
          <div
            className="profile-orders-loading"
          >
            <Clock size={26} />

            <span>
              CARGANDO TUS COMPRAS...
            </span>
          </div>
        </section>
      )}

      {/* ===================================================
          ERROR
      =================================================== */}

      {!loading && error && (
        <section
          className="profile-template-card"
        >
          <div
            className="profile-orders-error"
          >
            <XCircle size={28} />

            <div>
              <strong>
                NO FUE POSIBLE CARGAR LAS COMPRAS
              </strong>

              <span>
                {error}
              </span>
            </div>

            <button
              type="button"
              onClick={loadCheckouts}
            >
              REINTENTAR
            </button>
          </div>
        </section>
      )}

      {/* ===================================================
          EMPTY
      =================================================== */}

      {!loading &&
        !error &&
        sortedCheckouts.length === 0 && (
          <section
            className="profile-template-card"
          >
            <div
              className="profile-orders-empty"
            >
              <Package size={42} />

              <strong>
                TODAVÍA NO TIENES COMPRAS
              </strong>

              <span>
                Cuando realices tu primer checkout aparecerá aquí.
              </span>

              <button
                type="button"
                onClick={() =>
                  navigate("/products")
                }
              >
                VER PRODUCTOS
              </button>
            </div>
          </section>
        )}

      {/* ===================================================
          CHECKOUT LIST
      =================================================== */}

      {!loading &&
        !error &&
        sortedCheckouts.length > 0 && (
          <section
            className="profile-template-card"
          >

            {/* HEADER */}

            <div
              className="profile-card-title"
            >
              <Package size={21} />

              <div>
                <h2>
                  Historial de compras
                </h2>

                <p>
                  {sortedCheckouts.length} proceso
                  {sortedCheckouts.length === 1
                    ? ""
                    : "s"} de compra registrados
                </p>
              </div>
            </div>

            {/* LIST */}

            <div
              className="profile-orders-list"
            >
              {sortedCheckouts.map(
                (checkout) => {
                  const status =
                    getStatusConfig(
                      checkout.status
                    );

                  const StatusIcon =
                    status.icon;

                  return (
                    <article
                      className="profile-order-item"
                      key={checkout.id}
                    >

                      {/* ICON */}

                      <div
                        className="profile-order-icon"
                      >
                        <Package size={22} />
                      </div>

                      {/* MAIN */}

                      <div
                        className="profile-order-main"
                      >
                        <strong>
                          {checkout.checkout_number ||
                            `CHECKOUT #${checkout.id}`}
                        </strong>

                        <span>
                          {formatDate(
                            checkout.created_at
                          )}
                        </span>

                        <small>
                          {getDeliveryLabel(
                            checkout.delivery_type
                          )}
                        </small>
                      </div>

                      {/* TOTAL */}

                      <div
                        className="profile-order-total"
                      >
                        {formatMoney(
                          checkout.total
                        )}
                      </div>

                      {/* STATUS */}

                      <div
                        className={
                          `profile-order-status ${status.className}`
                        }
                      >
                        <StatusIcon size={15} />

                        {status.label}
                      </div>

                      {/* ACTIONS */}

                      <div
                        className="profile-order-actions"
                      >
                        <button
                          type="button"
                          onClick={() =>
                            openCheckout(
                              checkout
                            )
                          }
                          title="Ver detalle"
                          aria-label={
                            `Ver detalle de ${
                              checkout.checkout_number ||
                              `checkout #${checkout.id}`
                            }`
                          }
                        >
                          <ChevronRight size={19} />
                        </button>
                      </div>

                    </article>
                  );
                }
              )}
            </div>
          </section>
        )}

      {/* ===================================================
          CHECKOUT DETAIL MODAL
      =================================================== */}

      {selectedCheckout && (
        <div
          className="profile-modal-overlay"
          onMouseDown={closeDetail}
        >
          <section
            className="profile-modal"
            onMouseDown={(event) =>
              event.stopPropagation()
            }
          >

            {/* HEADER */}

            <header
              className="profile-modal-header"
            >
              <div>
                <span>
                  DEBUTS CROP · CHECKOUT
                </span>

                <h2>
                  {selectedCheckout.checkout_number ||
                    `CHECKOUT #${selectedCheckout.id}`}
                </h2>
              </div>

              <button
                type="button"
                onClick={closeDetail}
                aria-label="Cerrar detalle"
              >
                <X size={21} />
              </button>
            </header>

            {/* STATUS */}

            {(() => {
              const status =
                getStatusConfig(
                  selectedCheckout.status
                );

              const StatusIcon =
                status.icon;

              return (
                <div
                  className={
                    `profile-modal-status ${status.className}`
                  }
                >
                  <StatusIcon size={18} />

                  {status.label}
                </div>
              );
            })()}

            {/* TOTAL */}

            <div
              className="profile-modal-total"
            >
              <span>
                TOTAL
              </span>

              <strong>
                {formatMoney(
                  selectedCheckout.total
                )}
              </strong>
            </div>

            {/* INFO GRID */}

            <div
              className="profile-modal-grid"
            >

              {/* DATE */}

              <div>
                <small>
                  FECHA
                </small>

                <strong>
                  {formatDateTime(
                    selectedCheckout.created_at
                  )}
                </strong>
              </div>

              {/* PAYMENT */}

              <div>
                <small>
                  MÉTODO DE PAGO
                </small>

                <strong>
                  {getPaymentLabel(
                    selectedCheckout.payment_method
                  )}
                </strong>
              </div>

              {/* DELIVERY */}

              <div>
                <small>
                  ENTREGA
                </small>

                <strong>
                  {getDeliveryLabel(
                    selectedCheckout.delivery_type
                  )}
                </strong>
              </div>

              {/* PHONE */}

              <div>
                <small>
                  TELÉFONO
                </small>

                <strong>
                  {selectedCheckout.customer_phone ||
                    "NO REGISTRADO"}
                </strong>
              </div>

            </div>

            {/* =================================================
                DELIVERY
            ================================================= */}

            <div
              className="profile-modal-section"
            >
              <div
                className="profile-modal-section-title"
              >
                {selectedCheckout.delivery_type ===
                "home" ? (
                  <Truck size={18} />
                ) : (
                  <Store size={18} />
                )}

                <strong>
                  {selectedCheckout.delivery_type ===
                  "home"
                    ? "DATOS DEL DOMICILIO"
                    : "DATOS DE RECOGIDA"}
                </strong>
              </div>

              {selectedCheckout.delivery_type ===
              "home" ? (
                <div
                  className="profile-delivery-info"
                >
                  <div>
                    <MapPin size={18} />

                    <span>
                      {[
                        selectedCheckout.address,
                        selectedCheckout.neighborhood,
                        selectedCheckout.city,
                        selectedCheckout.department,
                      ]
                        .filter(Boolean)
                        .join(", ") ||
                        "DIRECCIÓN NO REGISTRADA"}
                    </span>
                  </div>

                  {selectedCheckout.address_reference && (
                    <p>
                      <strong>
                        Referencia:
                      </strong>{" "}
                      {selectedCheckout.address_reference}
                    </p>
                  )}
                </div>
              ) : (
                <div
                  className="profile-delivery-info"
                >
                  <div>
                    <Store size={18} />

                    <span>
                      {selectedCheckout.pickup_store_name ||
                        "TIENDA DEBUTS CROP"}
                    </span>
                  </div>

                  {selectedCheckout.pickup_store_address && (
                    <p>
                      {
                        selectedCheckout.pickup_store_address
                      }
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* =================================================
                CUSTOMER NOTES
            ================================================= */}

            {selectedCheckout.customer_notes && (
              <div
                className="profile-modal-section"
              >
                <div
                  className="profile-modal-section-title"
                >
                  <FileText size={18} />

                  <strong>
                    NOTAS
                  </strong>
                </div>

                <p
                  className="profile-modal-notes"
                >
                  {
                    selectedCheckout.customer_notes
                  }
                </p>
              </div>
            )}

            {/* =================================================
                PAYMENT PROOF
            ================================================= */}

            <div
              className="profile-modal-section"
            >
              <div
                className="profile-modal-section-title"
              >
                <CreditCard size={18} />

                <strong>
                  PAGO
                </strong>
              </div>

              <div
                className="profile-payment-info"
              >
                <div>
                  <span>
                    Método
                  </span>

                  <strong>
                    {getPaymentLabel(
                      selectedCheckout.payment_method
                    )}
                  </strong>
                </div>

                {selectedCheckout.payment_reference && (
                  <div>
                    <span>
                      Referencia
                    </span>

                    <strong>
                      {
                        selectedCheckout.payment_reference
                      }
                    </strong>
                  </div>
                )}

                {selectedCheckout.payment_proof && (
                  <a
                    href={
                      selectedCheckout.payment_proof
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="profile-payment-proof-link"
                  >
                    <FileText size={17} />

                    VER COMPROBANTE

                    <ExternalLink size={15} />
                  </a>
                )}
              </div>
            </div>

            {/* =================================================
                PIN
            ================================================= */}

            {selectedCheckout.verification_pin &&
              (
                selectedCheckout.status ===
                  "payment_approved" ||
                selectedCheckout.status ===
                  "confirmed" ||
                selectedCheckout.status ===
                  "processing" ||
                selectedCheckout.status ===
                  "ready"
              ) && (
                <div
                  className="profile-verification-pin"
                >
                  <ShieldCheck size={22} />

                  <div>
                    <span>
                      PIN DE VERIFICACIÓN
                    </span>

                    <strong>
                      {
                        selectedCheckout.verification_pin
                      }
                    </strong>

                    <small>
                      Preséntalo cuando corresponda a la entrega o recogida.
                    </small>
                  </div>
                </div>
              )}

            {/* =================================================
                ACTIONS
            ================================================= */}

            <div
              className="profile-modal-actions"
            >

              {/* TICKET */}

              <button
                type="button"
                className="profile-modal-button secondary"
                onClick={() => {
                  closeDetail();

                  openTicket(
                    selectedCheckout
                  );
                }}
              >
                <FileText size={18} />

                VER TICKET
              </button>

              {/* ROUTE */}

              {selectedCheckout.delivery_type ===
                "home" && (
                <button
                  type="button"
                  className="profile-modal-button primary"
                  onClick={() =>
                    openRoute(
                      selectedCheckout
                    )
                  }
                  disabled={
                    !buildGoogleMapsRoute(
                      selectedCheckout
                    )
                  }
                >
                  <MapPin size={18} />

                  VER RUTA
                </button>
              )}

              {/* CHECKOUT */}

              {[
                "pending",
                "payment_rejected",
              ].includes(
                selectedCheckout.status
              ) && (
                <button
                  type="button"
                  className="profile-modal-button primary"
                  onClick={() =>
                    navigate(
                      "/checkout"
                    )
                  }
                >
                  <CreditCard size={18} />

                  CONTINUAR CHECKOUT
                </button>
              )}

            </div>
          </section>
        </div>
      )}

      {/* =====================================================
          TICKET
      ===================================================== */}

      {ticketCheckout && (
        <div
          className="profile-modal-overlay profile-ticket-overlay"
          onMouseDown={closeTicket}
        >
          <section
            className="profile-ticket"
            onMouseDown={(event) =>
              event.stopPropagation()
            }
          >

            {/* TICKET HEADER */}

            <div
              className="profile-ticket-header"
            >
              <div
                className="profile-ticket-logo"
              >
                DEBUTS CROP
              </div>

              <span>
                DEBUTS CROP
              </span>

              <small>
                COMPROBANTE DE COMPRA
              </small>
            </div>

            {/* NUMBER */}

            <div
              className="profile-ticket-number"
            >
              <span>
                CHECKOUT
              </span>

              <strong>
                {
                  ticketCheckout.checkout_number ||
                  `#${ticketCheckout.id}`
                }
              </strong>
            </div>

            {/* DATE */}

            <div
              className="profile-ticket-row"
            >
              <span>
                FECHA
              </span>

              <strong>
                {formatDateTime(
                  ticketCheckout.created_at
                )}
              </strong>
            </div>

            {/* CUSTOMER */}

            <div
              className="profile-ticket-row"
            >
              <span>
                CLIENTE
              </span>

              <strong>
                {
                  ticketCheckout.customer_name ||
                  "CLIENTE"
                }
              </strong>
            </div>

            {/* PHONE */}

            <div
              className="profile-ticket-row"
            >
              <span>
                TELÉFONO
              </span>

              <strong>
                {
                  ticketCheckout.customer_phone ||
                  "NO REGISTRADO"
                }
              </strong>
            </div>

            {/* DELIVERY */}

            <div
              className="profile-ticket-row"
            >
              <span>
                ENTREGA
              </span>

              <strong>
                {getDeliveryLabel(
                  ticketCheckout.delivery_type
                )}
              </strong>
            </div>

            {/* ADDRESS */}

            {ticketCheckout.delivery_type ===
              "home" && (
              <div
                className="profile-ticket-address"
              >
                <span>
                  DIRECCIÓN
                </span>

                <strong>
                  {[
                    ticketCheckout.address,
                    ticketCheckout.neighborhood,
                    ticketCheckout.city,
                    ticketCheckout.department,
                  ]
                    .filter(Boolean)
                    .join(", ") ||
                    "DIRECCIÓN NO REGISTRADA"}
                </strong>
              </div>
            )}

            {/* STORE */}

            {ticketCheckout.delivery_type ===
              "store" && (
              <div
                className="profile-ticket-address"
              >
                <span>
                  TIENDA
                </span>

                <strong>
                  {
                    ticketCheckout.pickup_store_name ||
                    "DEBUTS CROP"
                  }
                </strong>

                {ticketCheckout.pickup_store_address && (
                  <small>
                    {
                      ticketCheckout.pickup_store_address
                    }
                  </small>
                )}
              </div>
            )}

            {/* PAYMENT */}

            <div
              className="profile-ticket-row"
            >
              <span>
                PAGO
              </span>

              <strong>
                {getPaymentLabel(
                  ticketCheckout.payment_method
                )}
              </strong>
            </div>

            {/* REFERENCE */}

            {ticketCheckout.payment_reference && (
              <div
                className="profile-ticket-row"
              >
                <span>
                  REFERENCIA
                </span>

                <strong>
                  {
                    ticketCheckout.payment_reference
                  }
                </strong>
              </div>
            )}

            {/* TOTAL */}

            <div
              className="profile-ticket-total"
            >
              <span>
                TOTAL
              </span>

              <strong>
                {formatMoney(
                  ticketCheckout.total
                )}
              </strong>
            </div>

            {/* STATUS */}

            {(() => {
              const status =
                getStatusConfig(
                  ticketCheckout.status
                );

              const StatusIcon =
                status.icon;

              return (
                <div
                  className={
                    `profile-ticket-status ${status.className}`
                  }
                >
                  <StatusIcon size={17} />

                  {status.label}
                </div>
              );
            })()}

            {/* PIN */}

            {ticketCheckout.verification_pin && (
              <div
                className="profile-ticket-pin"
              >
                <span>
                  PIN DE VERIFICACIÓN
                </span>

                <strong>
                  {
                    ticketCheckout.verification_pin
                  }
                </strong>
              </div>
            )}

            {/* FOOTER */}

            <div
              className="profile-ticket-footer"
            >
              <span>
                Gracias por comprar en Debuts Crop
              </span>

              <small>
                TU CULTIVO · TU ESTILO
              </small>
            </div>

            {/* =================================================
                TICKET ACTIONS
                ESTILIZADAS POR Profile.css
            ================================================= */}

            <div
              className="profile-ticket-actions"
            >
              <button
                type="button"
                className="profile-ticket-print-button"
                onClick={printTicket}
              >
                <Printer size={17} />

                IMPRIMIR
              </button>

              <button
                type="button"
                className="profile-ticket-close-button"
                onClick={closeTicket}
              >
                <X size={17} />

                CERRAR
              </button>
            </div>

          </section>
        </div>
      )}

    </ProfilePageLayout>
  );
}